import { HttpClient } from '@angular/common/http';
import {Injectable} from '@angular/core';

import {Observable} from 'rxjs';
import {catchError} from 'rxjs/operators';
import {StaticMapPayload} from '../types/StaticMapPayload';
import {ApiHelper} from './api.helper';
import {SearchState} from '../types/SearchState';

import {SearchHelperService} from './search-helper.service';
import {UserLocationState} from '../types/UserLocationState';
import {ToolsService} from './tools.service';
import {SearchResultsPayload} from '../types/SearchResultsPayload';
import {CareproviderMapItemTranslated} from '../types/CareproviderMapItemTranslated';
import {CareproviderMapItem} from '../types/CareproviderMapItem';
import {AppConfigService} from './app-config.service';

@Injectable()
export class MapsService {
  private maxMapSizeValue = 640;
  private maxUrlLength = 8000;

  constructor(
    private http: HttpClient,
    private apiHelper: ApiHelper,
    private searchHelper: SearchHelperService,
    private tools: ToolsService,
    private appConfigService: AppConfigService
  ) {}

  public GetSearchMapResults(
    searchState: SearchState,
    userLocationState: UserLocationState,
    bounds?: google.maps.LatLngBounds
  ): Observable<CareproviderMapItem[]> {
    const payload: SearchResultsPayload = this.tools.clone(
      this.searchHelper.generateSearchPayloadFromSearchState(searchState, !this.tools.isNil(bounds), false, false)
    );
    if (bounds) {
      payload.bounds = {
        nw: {
          lat: bounds.getNorthEast().lat(),
          lon: bounds.getSouthWest().lng(),
        },
        se: {
          lat: bounds.getSouthWest().lat(),
          lon: bounds.getNorthEast().lng(),
        },
      };
    }
    const params = this.tools.buildHttpParams(payload);
    return this.http
      .get<CareproviderMapItem[]>(this.appConfigService.appConfig.apiUrl + 'search/v2/searchmapdata', {params: params})
      .pipe(catchError((error) => this.apiHelper.handleError(error)));
  }

  public GetStaticMap(markers: CareproviderMapItemTranslated[], width: number, height: number): Observable<string> {
    return this.http
      .post<string>(
        this.appConfigService.appConfig.apiUrl + 'googlemaps',
        this.prepareMapPayload(markers, width, height)
      )
      .pipe(catchError((error) => this.apiHelper.handleError(error)));
  }

  public prepareMapPayload(markers: CareproviderMapItemTranslated[], width: number, height: number): StaticMapPayload {
    let query =
      'https://maps.googleapis.com/maps/api/staticmap?maptype=roadmap&style=feature:poi|visibility:off&' +
      this.getMapSizeString(width, height);
    query += this.getMapMarkersString(markers);
    query += '&key=' + this.appConfigService.appConfig.googleMapsApiKey;
    return {query};
  }

  private getMapMarkersString(markers: CareproviderMapItemTranslated[]): string {
    let markersString = '';
    markers.forEach((marker) => {
      if (markersString.length < this.maxUrlLength && marker.location.lat !== 0) {
        markersString += `&markers=size:small%7C${marker.location.lat},${marker.location.lng}`;
      }
    });
    return markersString;
  }

  private getMapSizeString(width: number, height: number): string {
    let sizeString = 'size=';
    if (width > this.maxMapSizeValue || height > this.maxMapSizeValue) {
      sizeString += this.getOversizedMapSizeString(width, height);
    } else {
      sizeString += `${width}x${height}`;
    }
    return sizeString;
  }

  private getOversizedMapSizeString(width: number, height: number): string {
    let sizeString = '';
    if (width / this.maxMapSizeValue > 2 || height / this.maxMapSizeValue > 2) {
      sizeString += this.getMoreThanTwiceOversizedMapSizeString(width, height);
    } else {
      sizeString += this.getLessThanTwiceOversizedMapSizeString(width, height);
    }
    return sizeString;
  }

  private getMoreThanTwiceOversizedMapSizeString(width: number, height: number): string {
    let destWidth = 0;
    let destHeight = 0;
    if (width / this.maxMapSizeValue > 2) {
      destWidth = this.maxMapSizeValue;
      destHeight = this.calculateHeight(width, height, destWidth);
    } else {
      destHeight = this.maxMapSizeValue;
      destWidth = this.calculateWidth(width, height, destHeight);
    }
    return `${destWidth}x${destHeight}&scale=2`;
  }

  private getLessThanTwiceOversizedMapSizeString(width: number, height: number): string {
    const ratio = width / this.maxMapSizeValue;
    const destWidth = Math.floor(width / ratio);
    const destHeight = Math.floor(height / ratio);
    return `${destWidth}x${destHeight}&scale=2`;
  }

  private calculateHeight(width: number, height: number, destWidth: number): number {
    return Math.floor((destWidth * height) / width);
  }

  private calculateWidth(width: number, height: number, destHeight: number): number {
    return Math.floor((destHeight * width) / height);
  }
}
